export enum TimeUnit {
	Weeks = "weeks",
	Days = "days",
	Hours = "hours",
	Minutes = "minutes",
	Seconds = "seconds",
	Milliseconds = "milliseconds"
}

export enum SingularTimeUnit {
	Week = "week",
	Day = "day",
	Hour = "hour",
	Minute = "minute",
	Second = "second",
	Millisecond = "millisecond"
}

export const TimeUnitDuration: { unit: TimeUnit, singularUnit: SingularTimeUnit, milliseconds: number }[] = [
	{
		unit: TimeUnit.Weeks,
		singularUnit: SingularTimeUnit.Week,
		milliseconds: 604800000
	},
	{
		unit: TimeUnit.Days,
		singularUnit: SingularTimeUnit.Day,
		milliseconds: 86400000
	},
	{
		unit: TimeUnit.Hours,
		singularUnit: SingularTimeUnit.Hour,
		milliseconds: 3600000
	},
	{
		unit: TimeUnit.Minutes,
		singularUnit: SingularTimeUnit.Minute,
		milliseconds: 60000
	},
	{
		unit: TimeUnit.Seconds,
		singularUnit: SingularTimeUnit.Second,
		milliseconds: 1000
	},
	{
		unit: TimeUnit.Milliseconds,
		singularUnit: SingularTimeUnit.Millisecond,
		milliseconds: 1
	}
]

export const GetTimeUnitDuration = (unit: TimeUnit | SingularTimeUnit) => {
	return TimeUnitDuration.find(u => u.unit === unit || u.singularUnit === unit)
}

export const ShortenTimeUnit = (unit: TimeUnit | string) => {
	switch (unit) {
		// Weeks
		case TimeUnit.Weeks: return "wks"
		case SingularTimeUnit.Week: return "wk"

		// Days
		case TimeUnit.Days: return unit
		case SingularTimeUnit.Day: return unit

		// Hours
		case TimeUnit.Hours: return "hrs"
		case SingularTimeUnit.Hour: return "hr"

		// Minutes
		case TimeUnit.Minutes: return "mins"
		case SingularTimeUnit.Minute: return "min"

		// Seconds
		case TimeUnit.Seconds: return "secs"
		case SingularTimeUnit.Second: return "sec"

		// Milliseconds
		case TimeUnit.Milliseconds:
		case SingularTimeUnit.Millisecond: return "ms"

		default:
			return unit
	}
}

const timeUnitMap: { [key: string]: string } = {
	// Weeks
	[TimeUnit.Weeks]: SingularTimeUnit.Week,
	"wks": "wk",

	// Days
	[TimeUnit.Days]: SingularTimeUnit.Day,

	// Hours
	[TimeUnit.Hours]: SingularTimeUnit.Hour,
	"hrs": "hr",

	// Minutes
	[TimeUnit.Minutes]: SingularTimeUnit.Minute,
	"mins": "min",

	// Seconds
	[TimeUnit.Seconds]: SingularTimeUnit.Second,
	"secs": "sec",

	// Milliseconds
	[TimeUnit.Milliseconds]: SingularTimeUnit.Millisecond,
	"ms": "ms"
}

export const TimeUnitToSingular = (unit: string) => {
	return timeUnitMap[unit] || unit
}

export const SingularTimeUnitToPlural = (unit: string): string => {
	const entry = Object.entries(timeUnitMap).find(([_, singular]) => singular === unit)
	return entry?.[0] || unit
}

/**
 * Converts time from one unit to another.
 * @param value The value to convert.
 * @param fromUnit The unit to convert from.
 * @param toUnit The unit to convert to.
 * @returns The converted value.
 * @throws An error if a time unit is invalid.
 */
export const ConvertTimeUnit = (value: number, fromUnit: TimeUnit | SingularTimeUnit, toUnit: TimeUnit | SingularTimeUnit): number => {
	const fromUnitInfo = GetTimeUnitDuration(fromUnit)
	const toUnitInfo = GetTimeUnitDuration(toUnit)

	if (!fromUnitInfo || !toUnitInfo) {
		throw new Error("Invalid time unit: " + fromUnit + "->" + toUnit)
	}

	// Convert to milliseconds first, then to target unit
	const milliseconds = value * fromUnitInfo.milliseconds
	return milliseconds / toUnitInfo.milliseconds
}
